import {Calculation, Numeric, Language, Translations, EnumerationsLogic} from "@salaxy/core";
/**
 * Helper class used to create RowsReport config for sxy-table and create tableData for ExcelHelpers(ng1)
 */
export class RowsReportConfigGenerator{
  /**
   * Generates config for SxyTable component
   * @param calcs Calculations for report
   * @param lang Language for report
   * @returns Configuration for SxyTable component
   */
  public static getConfig(calcs: Calculation[], lang: Language):{ options: any, include: any[], data: any[]} {
    const getData = (): any[] => {
      const result: any[] = [];
      calcs.forEach( calc => {
        calc.rows.forEach(row => {
          const resultRow: any = {};
          resultRow.worker = calc.worker.avatar.displayName;
          resultRow.socSec = calc.worker.paymentData.socialSecurityNumber;
          resultRow.message = row.message;
          resultRow.rowType = row.rowType;
          resultRow.rowTypeKind = {rowType: row.rowType, kind: row.data?.kind};
          resultRow.sourceId = row.sourceId;
          resultRow.count = row.count;
          resultRow.price = row.price;
          resultRow.total = (row.count ?? 1) * row.price;
          getDimensionsKeys().forEach(key => {
            resultRow[key] = row.accounting.dimensions.filter(x => x.id === key).map(dimension => dimension.value).join(", ");
          });
          result.push(resultRow);
        });
      });
      return result;
    };
    const getDimensionsKeys = (): string[] => {
      const result = [];
      const flattenedDimensions = calcs.map(calc => calc.rows.map(row => row.accounting.dimensions).flat()).flat();
      flattenedDimensions.forEach(dimension => {
        if(dimension && !result.includes(dimension.id)) {
          result.push(dimension.id);
        }
      });
      return result;
    };
    const getDimensionColumDefitions = (dimensions: string[]): any[] => {
      return dimensions.map(dimension => {
        return {title: dimension, field: dimension};
      });
    };
    const getConfig =  ():{ options: any, include: any[], data: any[]}  => {
      return {
        include: [
          {title: Translations.get("SALAXY.REPORTS.rowsReport.worker", null, lang), field: "worker", frozen: true, minWidth: 100},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.socsec", null, lang), field: "socSec"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.message", null, lang), field: "message"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.rowType", null, lang), field: "rowType", formatter: (cell: any) =>{
            return EnumerationsLogic.getEnumLabel("CalculationRowType", cell.getValue(), null, lang) ?? "";
          }},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.rowTypeKind", null, lang), field: "rowTypeKind", formatter: (cell: any) =>{
            const value = cell.getValue();
            if(!value?.rowType || !value?.kind) {
              return;
            }
            const rowType = (value.rowType as string).charAt(0).toUpperCase() + value.rowType.slice(1)  + "Kind";
            return EnumerationsLogic.getEnumLabel(rowType, value.kind) ?? "";
          }},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.sourceId", null, lang), field: "sourceId"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.count", null, lang), field: "count"},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.price", null, lang), field: "price", hozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }},
          {title: Translations.get("SALAXY.REPORTS.rowsReport.total", null, lang), field: "total", hozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }},
          ...getDimensionColumDefitions(getDimensionsKeys())
        ],
        options: {
          selectable: false,
          layout: "fitColumns"
        },
        data: getData()
      };
    };
    return getConfig();
  }

  /** Generates tableData for ExcelHelpers.export (ng1) */
  public static getTableData(calcs: Calculation[], lang: Language): any[] {
    const result = [];
    const getDimensionsKeys = (): string[] => {
      const result = [];
      const flattenedDimensions = calcs.map(calc => calc.rows.map(row => row.accounting.dimensions).flat()).flat();
      flattenedDimensions.forEach(dimension => {
        if(dimension && !result.includes(dimension.id)) {
          result.push(dimension.id);
        }
      });
      return result;
    };
    const dimensionKeys = getDimensionsKeys();
    const headers = [
      Translations.get("SALAXY.REPORTS.rowsReport.worker"),
      Translations.get("SALAXY.REPORTS.rowsReport.socsec", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.message", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.rowType", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.rowTypeKind", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.sourceId", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.count", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.price", null, lang),
      Translations.get("SALAXY.REPORTS.rowsReport.total", null, lang)
    ];
    dimensionKeys.forEach(key => {
      headers.push(key);
    });
    result.push(headers);
    calcs.forEach(calc => {
      calc.rows.forEach(irRow => {
        const row = [];
        row.push(calc.worker.avatar.displayName);
        row.push(calc.worker.paymentData.socialSecurityNumber);
        row.push(irRow.message);
        row.push(EnumerationsLogic.getEnumLabel("CalculationRowType", irRow.rowType, null, lang));
        row.push(irRow.data?.kind ? EnumerationsLogic.getEnumLabel((irRow?.rowType as string).charAt(0).toUpperCase() + irRow?.rowType?.slice(1)  + "Kind", irRow.data?.kind) : "");
        row.push(irRow.sourceId);
        row.push(irRow.count);
        row.push(irRow.price);
        row.push(Numeric.formatPrice((irRow.count ?? 1) * irRow.price));
        dimensionKeys.forEach(key => {
          const result = irRow.accounting.dimensions.filter(x => x.id === key).map(dimension => dimension.value).join(", ");
          row.push(result);
        });
        result.push(row);
      });
    });
    return result;
  }
}